<template>
    <BaseDialog v-if="video" v-model:open="open" width="wide" :title="video.title" :description="t('chat.tutorial.videoDialog.description')" show-close>
        <video
            class="aspect-video w-full rounded-lgr bg-black"
            crossorigin="anonymous"
            controls
            autoplay
            playsinline
            :src="video.src"
            :poster="video.poster"
            @play="handlePlay"
            @timeupdate="handleTimeUpdate"
            @ended="handleEnded">
            <track kind="captions" :src="video.captions" srclang="en" :label="t('chat.tutorial.videoDialog.captionsLabel')" default />
        </video>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseDialog from "~vue/components/BaseDialog.vue";
import { useNadiaTutorialAnalytics } from "~vue/composables/useNadiaTutorialAnalytics";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { watch } from "vue";
import { useI18n } from "vue-i18n";

import type { WalkthroughVideo } from "./walkthroughVideos";

interface Props {
    video: WalkthroughVideo | null;
    position: number;
}

const props = defineProps<Props>();
const { t } = useI18n();
const open = defineModel<boolean>("open");
const { logTutorialEvent } = useNadiaTutorialAnalytics();
const progressThresholds = [25, 50, 75] as const;
const progressLogged = new Set<number>();
let started = false;
let completed = false;

function resetPlaybackEvents() {
    started = false;
    completed = false;
    progressLogged.clear();
}

function handlePlay() {
    if (started || !props.video) {
        return;
    }

    started = true;
    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_VIDEO_STARTED, {
        video_id: props.video.id,
        category: props.video.category,
        position: props.position,
    });
}

function handleTimeUpdate(event: Event) {
    if (!props.video) {
        return;
    }

    const element = event.currentTarget as HTMLVideoElement;
    const progress = (element.currentTime / element.duration) * 100;
    for (const threshold of progressThresholds) {
        if (progress >= threshold && !progressLogged.has(threshold)) {
            progressLogged.add(threshold);
            logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_VIDEO_VIEWED, {
                video_id: props.video.id,
                category: props.video.category,
                position: props.position,
                progress: threshold,
            });
        }
    }
}

function handleEnded() {
    if (completed || !props.video) {
        return;
    }

    completed = true;
    logTutorialEvent(AmplitudeEventNames.NADIA_TUTORIAL_VIDEO_COMPLETED, {
        video_id: props.video.id,
        category: props.video.category,
        position: props.position,
    });
}

watch([open, () => props.video?.id], ([isOpen]) => {
    if (isOpen) {
        resetPlaybackEvents();
    }
});
</script>
